import React, { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { MDBContainer } from "mdb-react-ui-kit";
import { api } from "../../../services";
import Toolbar from "../../Toolbar";
import { DataTable, Tag } from "../../ui";
import { useTranslation } from "react-i18next";

const LOG_TYPE_VARIANT = {
  error: "error",
  warn: "warning",
  fatal: "error",
  log: "info",
};

const SystemLogs = () => {
  const { t, i18n } = useTranslation("translation");
  const isRtl = i18n.language === "ar";

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableSortColumn, setTableSortColumn] = useState("datetime");
  const [sortDirection, setSortDirection] = useState("desc");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    log_type: "",
    search: "",
  });

  const logTypeOptions = [
    { id: "", name: "All Logs" },
    { id: "log", name: "Info" },
    { id: "warn", name: "Warning" },
    { id: "error", name: "Error" },
    { id: "fatal", name: "Fatal" },
  ];

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      };
      const token = localStorage.getItem("adminToken");
      const response = await api.get("/system-logs", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(response.data.data || []);
      setPagination(response.data.pagination || pagination);
    } catch (err) {
      setError(t("Failed to fetch logs. Please try again later."));
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, filters]);

  const handleFilterChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return format(new Date(dateString), "MMM dd, yyyy HH:mm:ss");
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return "—";
      }
    }
    return String(value);
  };

  const columns = useMemo(
    () => [
      {
        id: "datetime",
        header: t("dateandtime"),
        sortable: true,
        render: (log) => formatDate(log.datetime),
      },
      {
        id: "log_type",
        header: t("type"),
        sortable: true,
        render: (log) => (
          <Tag variant={LOG_TYPE_VARIANT[log.log_type] || "neutral"}>
            {log.log_type}
          </Tag>
        ),
      },
      {
        id: "application",
        header: t("application"),
        sortable: true,
        render: (log) =>
          log.app_id ? (
            <div>
              <div>{log.app_id.application}</div>
              <div className="text-muted small">{log.app_id.status}</div>
            </div>
          ) : (
            "—"
          ),
      },
      {
        id: "store",
        header: t("store"),
        sortable: true,
        render: (log) =>
          log.store_id ? (
            <div>
              <div>{log.store_id.name}</div>
              <div className="text-muted small">{log.store_id.email}</div>
            </div>
          ) : (
            "—"
          ),
      },
      {
        id: "user",
        header: t("user"),
        sortable: true,
        render: (log) =>
          log.user_id ? (
            <div>
              <div>{log.user_id.username}</div>
              <div className="text-muted small">{log.user_id.email}</div>
            </div>
          ) : (
            "—"
          ),
      },
      {
        id: "service",
        header: t("service"),
        sortable: true,
        render: (log) => renderValue(log.controller_service),
      },
      {
        id: "details",
        header: t("details"),
        render: (log) => renderValue(log.log_details),
      },
      {
        id: "additional_data",
        header: t("additionaldata"),
        render: (log) => {
          if (!log.additional_data) return "—";
          const parts = [];
          if (
            log.additional_data.contextIds &&
            typeof log.additional_data.contextIds === "object"
          ) {
            Object.entries(log.additional_data.contextIds).forEach(
              ([key, value]) => {
                parts.push(`${key}: ${renderValue(value)}`);
              }
            );
          }
          if (log.additional_data.order_id) {
            parts.push(`order_id: ${log.additional_data.order_id}`);
          }
          return parts.length ? parts.join(", ") : "—";
        },
      },
    ],
    [t]
  );

  const sortedLogs = useMemo(() => {
    const list = [...logs];
    const dir = sortDirection === "asc" ? 1 : -1;
    const getVal = (log) => {
      switch (tableSortColumn) {
        case "datetime":
          return log.datetime ? new Date(log.datetime).getTime() : 0;
        case "log_type":
          return (log.log_type || "").toLowerCase();
        case "application":
          return (log.app_id?.application || "").toLowerCase();
        case "store":
          return (log.store_id?.name || "").toLowerCase();
        case "user":
          return (log.user_id?.username || "").toLowerCase();
        case "service":
          return (log.controller_service || "").toLowerCase();
        case "details":
          return (log.log_details || "").toLowerCase();
        default:
          return "";
      }
    };
    list.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === "number" && typeof bv === "number") {
        return dir * (av - bv);
      }
      return dir * String(av).localeCompare(String(bv));
    });
    return list;
  }, [logs, tableSortColumn, sortDirection]);

  return (
    <div className="admin-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("systemlogs")}</h1>

        <div className="oh-page-section">
          <Toolbar
            showFilter
            orderStatuses={logTypeOptions}
            activeTab={filters.log_type}
            setActiveTab={handleFilterChange("log_type")}
            showSearch
            searchPlaceHolder={t("Search logs...")}
            searchQuery={filters.search}
            handleSearch={handleFilterChange("search")}
          />
        </div>

        <div className="oh-page-section">
          <DataTable
            tableId="admin-system-logs"
            title={t("systemlogs")}
            columns={columns}
            data={error ? [] : sortedLogs}
            rowKey="_id"
            loading={loading}
            emptyState={error || t("No logs found")}
            isRtl={isRtl}
            sort={{
              column: tableSortColumn,
              direction: sortDirection,
              onChange: ({ column, direction }) => {
                setTableSortColumn(column);
                setSortDirection(direction);
              },
            }}
            pagination={{
              page: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              onPageChange: (page) =>
                setPagination((prev) => ({ ...prev, page })),
              onPageSizeChange: (size) =>
                setPagination((prev) => ({ ...prev, limit: size, page: 1 })),
              pageSizeOptions: [10, 25, 50, 100],
              showLabel: t("Show", { defaultValue: "Show" }),
              ofLabel: t("of", { defaultValue: "Of" }),
              resultsLabel: t("results", { defaultValue: "results" }),
              isRtl,
              previousLabel: t("previous"),
              nextLabel: t("next"),
            }}
          />
        </div>
      </MDBContainer>
    </div>
  );
};

export default SystemLogs;
